import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { range } from 'lodash';
import QRCode from 'react-qr-code';
import { OFFERS_DYNAMIC_TYPE } from '../../../constants';
import ReactIcon from '../../Icons';

export default function OfferPopup({ offer, onHide }) {
  const { getText } = Pollfish.ui;
  const openWebsite = useCallback(() => {
    Pollfish.mobile.interface.openWebsite(offer.redirectUrl);
  }, [offer]);

  const copyToClipboard = useCallback(() => {
    const text = offer.redirectUrl;
    if (navigator.clipboard) {
      return navigator.clipboard.writeText(text);
    }
    if (window.clipboardData && window.clipboardData.setData) {
      window.clipboardData.setData('Text', text);
      return Promise.resolve();
    }
    const input = document.createElement('input', { type: 'text' });
    input.value = text;
    document.body.append(input);
    input.focus();
    input.select();
    document.execCommand('copy');
    input.remove();
    return Promise.resolve();
  }, [offer]);

  const isSDK = Pollfish.helper.isSDK();
  const isRtlEnabled =
    document.querySelector('html').getAttribute('dir') === 'rtl';

  return (
    <div className="offerwall-offers-popup">
      <div className="offerwall-offers-popup-body">
        <button
          type="button"
          className="reset-button offerwall-offers-popup-actions-button-close"
          onClick={onHide}
          data-testid="offer-popup-hide"
        >
          <ReactIcon type="close" size={18} />
        </button>
        <div>
          <header className="offerwall-offers-popup-body-header">
            {!isRtlEnabled && (
              <Fragment>
                <span className="font-bold" data-testid="offer-popup-title">
                  {offer.title}
                </span>
                {offer.requirements && (
                  <Fragment>
                    <span className="margin-xxs-horizontal font-bold">
                      &middot;
                    </span>
                    <span className="text-sm">
                      <i>{offer.requirements}</i>
                    </span>
                  </Fragment>
                )}
              </Fragment>
            )}
            {isRtlEnabled && (
              <Fragment>
                {offer.requirements && (
                  <Fragment>
                    <span className="text-sm">
                      <i>{offer.requirements}</i>
                    </span>
                    <span className="margin-xxs-horizontal font-bold">
                      &middot;
                    </span>
                  </Fragment>
                )}
                <span className="font-bold" data-testid="offer-popup-title">
                  {offer.title}
                </span>
              </Fragment>
            )}
          </header>
          <div className="flex flex-align-items--center margin-md-top margin-md-btm">
            {offer.iconUrl && (
              <img
                className="margin-xs-right offerwall-offers-popup-icon"
                src={offer.iconUrl}
                alt={offer.title}
              />
            )}
            <div className="flex-1">
              <h1 className="text-xxl font-bold margin-reset line-height-reset">
                {offer.pointsValue}
              </h1>
              <p className="margin-reset" data-testid="offer-reward-currency">
                {offer.pointsName}
              </p>
            </div>
          </div>
          <span className="text-grey-light">{offer.description}</span>
        </div>
        <div className="divider margin-md-top margin-md-btm"></div>
        <div className="flex text-sm flex-wrap">
          <span className="text-grey-light">
            {getText('offerwall.offers.completed_activities')}:
          </span>
          <span className="font-bold margin-xxs-left offer-rtl-text-xxs">
            0/{offer.totalSubtasks}
          </span>
          <span className="margin-xxs-horizontal text-grey-light font-bold">
            &middot;
          </span>
          <span className="text-grey-light">
            {(getText('offerwall.offers.earned') || '').replace(
              'XXX',
              offer.pointsName,
            )}
            :
          </span>
          <span className="font-bold margin-xxs-left offer-rtl-text-xxs">
            0/{offer.pointsValue}
          </span>
        </div>
        <div className="flex margin-xs-top margin-md-btm">
          {range(offer.totalSubtasks).map((el, index) => (
            <div
              key={el}
              className={`offerwall-offers-popup-bar-step${
                index < offer.subtasksCompleted
                  ? ' offerwall-offers-popup-bar-step-completed'
                  : ''
              }`}
            ></div>
          ))}
        </div>
        <div className="display-block">
          {(offer.subtasks || []).map((subtask, index) => (
            <div
              key={subtask.subtaskId}
              className={`flex flex-column margin-xxs-btm text-sm offerwall-offers-popup-task${
                index < (offer.subtasksCompleted || 0)
                  ? ' offerwall-offers-popup-task-completed'
                  : ''
              }`}
            >
              <div className="flex flex-1">
                <div
                  className={`offerwall-offers-popup-task-icon${
                    index < (offer.subtasksCompleted || 0)
                      ? ' offerwall-offers-popup-task-icon-completed'
                      : ''
                  }`}
                ></div>
                <div className="flex flex-1 flex-justify--space-between">
                  {index < (offer.subtasksCompleted || 0) && (
                    <span>
                      <s>{subtask.title}</s>
                    </span>
                  )}
                  {index >= (offer.subtasksCompleted || 0) && (
                    <span>{subtask.title}</span>
                  )}
                  {subtask.dynamicDisplayType &&
                    subtask.dynamicDisplayType ===
                      OFFERS_DYNAMIC_TYPE.COMMISSION && (
                      <span data-testid="offer-popup-subtask-points">
                        <b>+{subtask.pointsValue}</b> {offer.pointsName}/$
                        {subtask.dynamicPayout}
                      </span>
                    )}
                  {subtask.dynamicDisplayType &&
                    subtask.dynamicDisplayType ===
                      OFFERS_DYNAMIC_TYPE.UP_TO && (
                      <span data-testid="offer-popup-subtask-points">
                        {getText('offerwall.offers.upTo')}:{' '}
                        <b>{subtask.pointsValue}</b> {offer.pointsName}
                      </span>
                    )}
                  {!subtask.dynamicDisplayType && (
                    <span data-testid="offer-popup-subtask-points">
                      {offer.pointsName}: <b>+{subtask.pointsValue}</b>
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        {isSDK && (
          <button
            type="button"
            onClick={openWebsite}
            className="reset-button offerwall-offers-popup-actions-button-sdk"
          >
            {(getText('offerwall.offers.moreReward') || '').replace(
              'XXXX',
              offer.pointsName,
            )}
          </button>
        )}
        {!isSDK && (
          <Fragment>
            <div className="divider margin-md-top margin-md-btm"></div>
            <div className="text-sm offerwall-offers-popup-actions">
              <div className="display-block margin-md-btm">
                <span>{getText('offerwall.offers.paste')}</span>
                <div className="offerwall-offers-popup-actions-link-wrapper">
                  <button
                    type="button"
                    className="reset-button offerwall-offers-popup-actions-link"
                    onClick={openWebsite}
                    data-testid="offer-popup-url"
                  >
                    {offer.redirectUrl}
                  </button>
                  <button
                    className="reset-button offerwall-offers-popup-actions-button"
                    type="button"
                    onClick={copyToClipboard}
                    data-testid="offer-popup-copy"
                  >
                    {getText('offerwall.offers.copy')}
                  </button>
                </div>
              </div>
              <div className="flex flex-justify--space-between flex-align-items--center">
                <span>{getText('offerwall.offers.scan')}</span>
                <div
                  className="offerwall-offers-popup-actions-qr"
                  data-testid="offer-popup-qr"
                >
                  <QRCode
                    level="H"
                    size={100}
                    value={offer.redirectUrl || ''}
                  />
                </div>
              </div>
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
}

OfferPopup.propTypes = {
  offer: PropTypes.any.isRequired,
  onHide: PropTypes.func.isRequired,
};
